import React, { memo, ReactElement, useEffect, useRef } from 'react';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert';
import alertTypes from '@jbhi-fi/lanyard-ui/components/Alert/types/alert.type';
import DOMPurify from 'dompurify';
import { styles } from './Messages.css';

type MessageProps = {
  message: Message;
  inline?: boolean;
  className?: string;
  showIcon?: boolean;
  showHeading?: boolean;
  leftComponent?: ReactElement;
  key?: string;
};

export type Message = {
  Message: string;
  MessageType: 'Important' | 'Warning' | 'General' | 'Critical' | 'Neutral';
  MessageHeading?: string;
  handleClick?: () => void;
};

const messageAlertTypeMap: {
  Important: alertTypes;
  Warning: alertTypes;
  General: alertTypes;
  Critical: alertTypes;
  Neutral: alertTypes;
} = {
  Important: 'warning',
  Warning: 'warning',
  General: 'info',
  Critical: 'error',
  Neutral: 'neutral',
};

const MessageItem = memo(
  ({
    message,
    inline = false,
    className = null,
    showIcon = true,
    showHeading = true,
    leftComponent = null,
  }: MessageProps): JSX.Element | null => {
    const messageBodyRef = useRef<HTMLDivElement>(null);

    // Parse message body
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(message?.Message ?? '', 'text/html');

    const hasImage = parsedHtml.body.querySelector('img');
    const paragraphs = parsedHtml.body.querySelectorAll('p');
    const hasParagraphs = paragraphs.length;
    const links = parsedHtml.body.querySelectorAll('a');
    const firstStrong = parsedHtml.body.querySelector('strong');

    let heading = showHeading ? message.MessageHeading || undefined : undefined;

    // Event handler for "More information" link
    const handleMoreInfoClick = (event: MouseEvent) => {
      event.preventDefault();
      message.handleClick && message.handleClick();
    };

    // Elevate first strong tag to Alert heading, remove
    if (showHeading && !heading && firstStrong && !hasImage && message?.MessageType !== 'Warning') {
      heading = firstStrong.textContent?.trim();
      firstStrong.remove();
    }

    // If Warning, set fixed title
    if (showHeading && !heading && message?.MessageType === 'Warning') {
      heading = window?.Jbhifi?.pdpAlerts?.ims_warning_heading || null;
    }

    // Remove any image style attributes
    if (hasImage) {
      hasImage.removeAttribute('style');
      hasImage.removeAttribute('class');
    }

    // Remove all empty paragraphs and any style attributes
    if (hasParagraphs) {
      paragraphs.forEach((paragraph: HTMLElement) => {
        if (paragraph.innerHTML === '' || paragraph.innerHTML === '&nbsp;' || paragraph.innerHTML === '<br>') {
          paragraph.remove();
        } else {
          paragraph.removeAttribute('style');
          paragraph.removeAttribute('class');
        }
      });
    }

    // Remove any link style attributes
    if (links.length) {
      links.forEach((link: HTMLElement) => {
        link.removeAttribute('style');
        link.removeAttribute('class');
      });
    }

    // Sanitize message body HTML
    let sanitizedHtml = DOMPurify.sanitize(parsedHtml.body.innerHTML, {
      ALLOWED_TAGS: ['p', 'strong', 'a', 'img', 'br', 'ul', 'li'],
    });

    // Remove trailing dot from image only messages
    if (hasImage && parsedHtml?.body?.textContent?.trim() === '.') {
      sanitizedHtml = sanitizedHtml.replace('>.', '>');
    }

    // Add event listener to "More information" link
    useEffect(() => {
      if (!messageBodyRef.current || !message.handleClick) return;
      const messageBodyElement = messageBodyRef.current;
      if (messageBodyElement) {
        const moreInfoLinks = messageBodyElement.querySelectorAll('a');
        const moreInfoLink = Array.from(moreInfoLinks).find(link =>
          link?.getAttribute('href')?.includes('#refurbishedInfo')
        );
        if (moreInfoLink) {
          moreInfoLink.addEventListener('click', handleMoreInfoClick);
          moreInfoLink.classList.add(styles.moreInfoLink);
        }

        // Cleanup event listener on component unmount
        return () => {
          if (moreInfoLink) {
            moreInfoLink.removeEventListener('click', handleMoreInfoClick);
          }
        };
      }
    }, [sanitizedHtml]);

    if (!message?.Message) {
      return null;
    }

    // Wrap message in paragraph if none exists
    const MessageBody = hasParagraphs ? (
      <div ref={messageBodyRef} className={styles.messageBody} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    ) : (
      <div ref={messageBodyRef} className={styles.messageBody}>
        <p dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      </div>
    );

    // Return message body without Alert wrapper if image
    if (hasImage) {
      return MessageBody;
    }

    const alertProps: {
      showIcon: boolean;
      inline: boolean;
      classes: string;
      leftComponent?: ReactElement;
    } = {
      showIcon,
      inline,
      classes: [className, inline && styles.inline].join(' '),
    };

    if (leftComponent) alertProps.leftComponent = leftComponent;

    return (
      <Alert type={messageAlertTypeMap[message.MessageType]} heading={heading} {...alertProps} content={MessageBody} />
    );
  }
);

MessageItem.displayName = 'MessageItem';

export default MessageItem;
